import { Component, Input, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { SegmentOneTimeFilters } from '@models/rate-card/segment-one-time-filters';
import { SegmentRecurringFilters } from '@models/rate-card/segment-recurring-filters';
import {
    AddSegmentPopupReturn,
    EditableSegment,
    NewDynamicSegments,
    NewSegmentData,
    RateCardMetricRowV2,
    SegmentPopupProps,
    NewSegmentMultiStationData,
} from '@models/rate-card/rate-card';
import moment from 'moment';
import { BooleanFilter, FilterTemplate, SelectFilter, TimeFilter } from '@models/filter-types';
import { SegmentItem } from '@models/lookup';
import { AuthService } from '@services/auth/auth.service';
import { LookupV2Service } from '@services/lookup-v2/lookup-v2.service';
import { NotificationService } from '@services/notification/notification.service';
import { Segmentation, UiConfig } from '@models/config';
import { MessageDialogComponent } from '@shared/components/message-dialog/message-dialog.component';
import { RateCardService } from '@services/rate-card/rate-card.service';

@Component({
    template: '',
    standalone: false,
})
export abstract class SegmentBaseComponent implements OnInit {
    @Input() rowData!: SegmentPopupProps;
    @Input() segmentData: EditableSegment = null;
    @Input() dialogRef!: MatDialogRef<unknown>;
    @Input() isOneTimeSegment!: boolean;
    config!: UiConfig;
    addToPolitical: BooleanFilter;
    addToDynamic: BooleanFilter;

    constructor(
        protected authSvc: AuthService,
        protected lookupSvc: LookupV2Service,
        protected snackBarSvc: NotificationService,
        protected rateCardSvc: RateCardService,
    ) {
        this.config = this.authSvc.getConfig();
        this.addToPolitical = new BooleanFilter('Add Program to Political Rate Card')
            .Slider(true)
            .LabelPosition('after')
            .Toggle(!this.config.roleCapability.rate_optics.political_rate_card.add_segment);

        this.addToDynamic = new BooleanFilter('Add Program to Dynamic Rate Card')
            .Slider(true)
            .LabelPosition('after')
            .Toggle(!this.config.roleCapability.rate_optics.dynamic_rate_card.add_segment);
    }

    ngOnInit(): void {
        this.addToPolitical.Value = !!(this.segmentData && this.segmentData.isPolitical);
        this.addToDynamic.Value = !!(this.segmentData && this.segmentData.isDynamic);

        // Show hint text if in edit mode and program was previously added to other rate card
        if (this.addToPolitical.Value) {
            this.addToPolitical.Hint('This will remove the program from the Political Rate Card');
            this.addToPolitical.showHint = false;
            this.addToPolitical.OnChange((val) => {
                this.addToPolitical.showHint = !val;
            });
        }
        if (this.addToDynamic.Value) {
            this.addToDynamic.Hint('This will remove the program from the Dynamic Rate Card');
            this.addToDynamic.showHint = false;
            this.addToDynamic.OnChange((val) => {
                this.addToDynamic.showHint = !val;
            });
        }
    }

    generateDynamicSegmentValues(filter: SegmentOneTimeFilters | SegmentRecurringFilters) {
        const dynamicSegments: NewDynamicSegments[] = [];

        let groupSegmentation = this.config.groupSegmentation[this.rowData.channelGroupId]?.rate_card || [];
        const timeSegmentation = groupSegmentation.filter((x) => x.is_start_time || x.is_end_time);
        const dowSegmentation = groupSegmentation.filter((x) => x.is_dow);
        groupSegmentation = groupSegmentation.filter((x) => !(x.is_dow || x.is_start_time || x.is_end_time));

        for (const segment of groupSegmentation) {
            const dynamicSegment = this.generateNewStringDynamicSegment(segment, filter);
            dynamicSegments.push(dynamicSegment);
        }
        for (const segment of timeSegmentation) {
            const dynamicSegment = this.generateNewTimeDynamicSegment(segment, filter);
            dynamicSegments.push(dynamicSegment);
        }
        for (const segment of dowSegmentation) {
            const dynamicSegment = this.generateNewDowDynamicSegment(segment, filter);
            dynamicSegments.push(dynamicSegment);
        }

        return dynamicSegments;
    }

    generateNewTimeDynamicSegment(segment: Segmentation, filter: FilterTemplate) {
        let segmentFormat = this.config.customerConfig.segment_time_format
            ? (this.config.customerConfig.segment_time_format as string)
            : 'hh:mm A';
        let trimSegmentTimeMeridiem = false;
        if (segmentFormat.includes('-1')) {
            segmentFormat = segmentFormat.replace('-1', '');
            trimSegmentTimeMeridiem = true;
        }
        let startTime = null;
        let endTime = null;
        if (segment.is_start_time) {
            startTime = moment((filter.dynamicFilters.startTimeFilter as TimeFilter).Value).format(segmentFormat);
            if (trimSegmentTimeMeridiem) {
                startTime = startTime.slice(0, -1);
            }
        }

        if (segment.is_end_time) {
            endTime = moment((filter.dynamicFilters.endTimeFilter as TimeFilter).Value).format(segmentFormat);
            if (trimSegmentTimeMeridiem) {
                endTime = endTime.slice(0, -1);
            }
        }

        const dynamicSegment: NewDynamicSegments = {
            segmentType: segment.segment_type,
            segmentValue: null,
            startTime,
            endTime,
        };
        if (segment.is_start_time && segment.is_end_time) {
            dynamicSegment.segmentValue = dynamicSegment.startTime + '-' + dynamicSegment.endTime;
        } else if (segment.is_start_time) {
            dynamicSegment.segmentValue = dynamicSegment.startTime;
        } else if (segment.is_end_time) {
            dynamicSegment.segmentValue = dynamicSegment.endTime;
        }
        return dynamicSegment;
    }

    // @ts-expect-error TS7053: implicit any
    generateNewStringDynamicSegment(segment: Segmentation, filter) {
        const segmentFilterValue = filter.dynamicFilters[segment.segment_type].Value;
        let dynamicSegment: NewDynamicSegments = {
            segmentType: segment.segment_type,
            segmentValue: filter.dynamicFilters[segment.segment_type].transform(segmentFilterValue),
        };
        const segmentIds = (filter.dynamicFilters[segment.segment_type] as SelectFilter<SegmentItem>)?.options.find(
            (seg) => seg.name === dynamicSegment.segmentValue,
        )?.ids;
        if (segmentIds) {
            dynamicSegment = { ...dynamicSegment, ...{ segmentId: segmentIds.find((id) => id > 0) } };
        }
        return dynamicSegment;
    }

    saveAndCloseAddSegmentPopup(newRowData: NewSegmentData[]) {
        const rowSaveData = {
            rateCardTab: this.rowData.rateCardTab,
            isDynamic: this.rowData.isDynamic || this.addToDynamic.Value,
            isPolitical: this.rowData.isPolitical || this.addToPolitical.Value,
            channelId: this.rowData.channelId,
            spotTypeId: this.rowData.spotTypeId,
            source: this.rowData.isPolitical ? 'political' : 'dynamic',
        };

        const parentSegmentData = {
            daypartId: this.segmentData ? this.segmentData.parentSegmentData.daypartId : this.rowData.daypartId,
            dynamicSegmentation: this.segmentData
                ? this.segmentData.parentSegmentData.dynamicSegmentation
                : this.rowData.parentDynamicSegmentation,
        };

        const body: AddSegmentPopupReturn = {
            reloadParams: this.rowData.reloadParams,
            ...rowSaveData,
            parentSegmentData,
            newSegments: newRowData,
        };
        const textDialogRef = this.rowData.matDialog.open(MessageDialogComponent, {
            disableClose: true,
            data: {
                message: 'Please wait while this program is added to your Rate Card',
            },
        });
        const saveFunc = this.segmentData
            ? (args: AddSegmentPopupReturn) => this.rateCardSvc.editSegment(args)
            : (args: AddSegmentPopupReturn) => this.rateCardSvc.saveUserSegment(args);
        const onSaveText = this.segmentData ? 'edited' : 'created';
        const onFailText = this.segmentData ? 'edit' : 'create';
        saveFunc(body).subscribe(
            (rows: RateCardMetricRowV2[]) => {
                this.rowData.getRateCardSegments();
                this.snackBarSvc.openSnackBar(`Row successfully ${onSaveText}`, 'success');
                this.rowData.afterSave(rows);
                textDialogRef.close();
                this.dialogRef.close();
            },
            () => {
                this.snackBarSvc.openSnackBar(`Failed to ${onFailText} row`, 'error');
                textDialogRef.close();
            },
        );
    }

    close() {
        this.dialogRef.close();
    }

    commonSaveFunc(newRowData: NewSegmentMultiStationData[]) {
        const textDialogRef = this.rowData.matDialog.open(MessageDialogComponent, {
            disableClose: true,
            data: {
                message: 'Please wait while this program is added to your Rate Card',
            },
        });
        this.rateCardSvc.saveBulkUserOneTimeSegment({ data: newRowData }).subscribe(
            (rows: RateCardMetricRowV2[]) => {
                this.rowData.getRateCardSegments();
                this.snackBarSvc.openSnackBar(`Programs successfully created`, 'success');
                this.rowData.afterSave(rows);
                textDialogRef.close();
                this.dialogRef.close();
            },
            () => {
                this.snackBarSvc.openSnackBar(`Failed to create programs`, 'error');
                textDialogRef.close();
            },
        );
    }

    // @ts-expect-error TS7053: implicit any
    abstract generateNewDowDynamicSegment(segment: Segmentation, filter: SegmentOneTimeFilters | SegmentRecurringFilters);
}
